import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchRaw } from "../../lib/api-client";
import { toApiFailure } from "../../lib/errors";
import type { components } from "../../lib/api-types.gen";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type InAppNotification = components["schemas"]["InAppNotification"];

export const notificationKeys = {
  all: ["notifications"] as const,
  inApp: ["notifications", "in-app"] as const,
};

/**
 * GET /notifications/in-app (F-014/AC-015, KOMP-107): the current user's
 * undismissed reminder banners, rendered German server-side, oldest first.
 * Polled every 5 minutes and on window focus so reminders created while a
 * session stays open still surface without a re-login. A failed fetch must
 * never disturb the app - the banner component keeps errors silent.
 *
 * Every one of these reads is the surface's doing, never the person's, so it
 * carries `X-Background: 1` ([NF-008]): a page left open with its poll
 * running still reaches the idle limit.
 */
export function useInAppNotifications() {
  return useQuery({
    queryKey: notificationKeys.inApp,
    queryFn: () => apiFetch<InAppNotification[]>("/notifications/in-app", undefined, { background: true }),
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * POST /notifications/in-app/{id}/dismiss (204, no body - apiFetchRaw). A 409
 * means "already dismissed" (e.g. in another tab) and counts as success; the
 * list invalidation reconciles either way.
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      try {
        await apiFetchRaw(`/notifications/in-app/${id}/dismiss`, {
          method: "POST",
        });
      } catch (error) {
        if (toApiFailure(error).status !== 409) throw error;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inApp });
    },
  });
}
